import React, { useEffect, useState } from "react";
import { StatusCode } from "api/protocols";
import "../TeeOnPortalBooking/teeOnPortalBooking.scss";
import "./teeOnBookingConfirmation.scss";
import OrderSummaryLineItem from "components/bookingEngine/orderSummaryLineItem/OrderSummaryLineItem";
import { useHistory } from "react-router";
import { UserActive } from "api/rpc";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import Card from "components/card/Card";
import { ButtonNew as Button } from "components/buttonNew";
import ReactDOM from "react-dom";
import { LocaleCurrency } from "helpers/Locale";
import { convertTime, formatDateFromString } from "helpers/Helpers";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { ITaxLine } from "components/OrderSummary/OrderSummary";
import { GetCart } from "api/rpc/2024-04/cart";
import { GetBooking } from "api/rpc/2024-04/customer/booking";
import { GetCustomerPaymentMethods } from "api/rpc/2024-04/customer/customer";
import { IFacilityPortalExtended, useTeeOnPortalContext } from "../TeeOnPortalContext";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { DeleteFacilityFavourite, PostFacilityFavourite } from "api/rpc/2024-04/customer/facility";
import { sortFacilities } from "redux/reducers/clientPortal";
import classNames from "classnames";
import Spin from "components/spin/spin";

export default function TeeOnPortalBookingConfirmation(props: any) {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const [activeUser, setActiveUser] = useState(false);
  const [favouriteLoading, setFavouriteLoading] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cartState, setCartState] = useState<Record<string, any>>(null);
  const [editBookingState, setEditBookingState] = useState<boolean>(true);
  // June 6, 2022
  const editDate = new Date("2022-06-06T00:00:00.000000Z").toUTCString();

  const queryString = window.location.search;
  const bookingToken = new URLSearchParams(queryString).get("token");
  const facilityShortName = new URLSearchParams(queryString).get("facility");

  useEffect(() => {
    void getCustomerLoggedIn();
    void setCart();
    if (teeOnPortalState?.selectedTeeTime?.booking) {
      void checkBookingEditTime(teeOnPortalState?.selectedTeeTime?.booking);
    }
    if (
      (teeOnPortalState?.selectedTeeTime?.teeTime?.credit_card_required ||
        teeOnPortalState?.selectedTeeTime?.teeTime?.pre_paid_required) &&
      teeOnPortalState?.selectedTeeTime?.booking?.paymentMethodId
    ) {
      void getCustomerPaymentMethod();
    }
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (
      teeOnPortalState?.user === null &&
      teeOnPortalState?.selectedTeeTime === null &&
      facilityShortName &&
      bookingToken
    ) {
      //Page was refreshed, get client & booking information
      void loadBookingInformation();
    }
  }, [teeOnPortalState?.user]);

  function setCart() {
    if (teeOnPortalState?.selectedTeeTime?.booking) {
      setCartState(teeOnPortalState?.selectedTeeTime?.booking?.cart);
    }
  }

  async function getCustomerLoggedIn() {
    // Check if user is logged in
    if (teeOnPortalState?.user === null) {
      dispatch(enqueue());
      const activeUserRes = await UserActive(false);
      dispatch(dequeue());
      if (activeUserRes.status === StatusCode.OK) {
        ReactDOM.unstable_batchedUpdates(() => {
          setActiveUser(true);
          updateTeeOnPortal({ user: activeUserRes?.data });
        });
      } else {
        //User is not logged in, send them back to the home page
        setActiveUser(false);
        dispatch(showError(t("User is not logged in. Please log in and try again.")));
        void backToTeeTimes();
      }
    } else {
      setActiveUser(true);
    }
  }

  async function loadBookingInformation() {
    dispatch(enqueue());
    const [facilityRes, bookingRes] = await Promise.all([
      GetFacilityGuest({ short_name: facilityShortName }, false),
      GetBooking({ token: bookingToken, extended: true }, false),
    ]);
    if (facilityRes?.status !== StatusCode.OK || bookingRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading confirmation")));
      dispatch(dequeue());
      void backToTeeTimes();
      return;
    }

    void checkBookingEditTime(bookingRes.data[0]);

    const teeTime = bookingRes.data[0]?.tee_time;

    const cartRes = await GetCart({ id: bookingRes.data[0]?.cart_id }, false);
    if (cartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading confirmation page.")));
      dispatch(dequeue());
      void backToTeeTimes();
      return;
    }

    let convertedStartTime: string = teeTime.start_time;
    if (teeTime?.start_time) {
      convertedStartTime = convertTime(teeTime.start_time);
    }

    const formatDate = formatDateFromString(teeTime.date);
    const formatTime = convertTime(teeTime?.start_time);
    dispatch(dequeue());

    ReactDOM.unstable_batchedUpdates(() => {
      updateTeeOnPortal({
        selectedTeeTime: {
          teeTime: {
            ...teeTime,
            formattedDate: formatDate,
            formattedStartTime: formatTime,
          },
          lock: null,
          facility: facilityRes?.data[0],
          booking: bookingRes?.data[0],
        },
      });
      setCartState(cartRes?.data[0]);
    });
  }

  function backToTeeTimes() {
    updateTeeOnPortal({ selectedTeeTime: null });
    history.push(`/booking/teetimes${facilityShortName ? "/".concat(facilityShortName) : ""}`);
  }

  function editBooking() {
    const address = `/tee-on/portal/bookings/${teeOnPortalState?.selectedTeeTime?.booking?.id}/edit`;
    window.open(address);
  }

  async function getCustomerPaymentMethod() {
    const paymentMethodRes = await GetCustomerPaymentMethods(
      {
        id: teeOnPortalState?.selectedTeeTime?.booking?.paymentMethodId,
        client_id: teeOnPortalState?.selectedTeeTime?.facility?.client_id,
      },
      true,
    );
    if (paymentMethodRes.status !== StatusCode.OK) {
      console.log("Error getting payment method");
      return;
    }

    if (paymentMethodRes.data?.length >= 1) {
      setPaymentMethod(paymentMethodRes.data[0]);
    }
  }

  function checkBookingEditTime(booking: Record<string, any>) {
    const completedAtDate = new Date(booking?.completed_at).toUTCString();

    const currentDateUTCString = new Date().toUTCString();

    const teeTimeDateSetUp = new Date(String(booking?.tee_time?.date) + "T" + String(booking?.tee_time?.start_time));
    //Set date-time based on facility timezone
    const teeTimeDateTimezoneString = new Date(teeTimeDateSetUp).toLocaleString("en-US", {
      timeZone: booking?.tee_time?.facility?.timezone,
    });
    const teeTimeEditTime = new Date(teeTimeDateTimezoneString);

    teeTimeEditTime.setHours(teeTimeEditTime.getHours() - booking?.tee_sheet_settings?.web_edit_hours);

    const teeTimeEditTimeUTCString = teeTimeEditTime.toUTCString();

    if (
      Date.parse(completedAtDate) <= Date.parse(editDate) ||
      Date.parse(currentDateUTCString) >= Date.parse(teeTimeEditTimeUTCString)
    ) {
      setEditBookingState(false);
    }
    return;
  }

  async function handleAddToFavourites() {
    const facility = teeOnPortalState?.selectedTeeTime?.facility;
    setFavouriteLoading(true);
    const favRes = await PostFacilityFavourite({ facility_id: facility?.id }, false);
    setFavouriteLoading(false);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding course to favourites")));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess(t("Successfully added course to favourites")));
  }

  async function handleRemoveFromFavourites() {
    const facility = teeOnPortalState?.selectedTeeTime?.facility;
    setFavouriteLoading(true);
    const favRes = await DeleteFacilityFavourite({ facility_id: facility?.id }, false);
    setFavouriteLoading(false);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing course from favourites")));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess(t("Successfully removed course from favourites")));
  }

  function updateFavouriteStatus(selectedFacility: IFacilityPortalExtended) {
    const updatedFacilities = teeOnPortalState?.facilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const updatedSelectedFacilities = teeOnPortalState?.selectedFacilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const selectedTeeTime = {
      ...teeOnPortalState?.selectedTeeTime,
      facility: { ...selectedFacility, favourite: !selectedFacility?.favourite },
    };
    const sortedFacilities = sortFacilities([...updatedFacilities]);
    updateTeeOnPortal({
      facilities: sortedFacilities,
      selectedTeeTime: selectedTeeTime,
      selectedFacilities: updatedSelectedFacilities,
    });
  }

  return (
    activeUser &&
    cartState && (
      <>
        <div className="tee-on-booking-portal-booking-background">
          <div className="tee-on-booking-portal-bc-facility-container">
            <div className="tee-on-booking-portal-booking-timer-facility-container">
              <span className="tee-on-booking-portal-bc-facility-logo">
                {teeOnPortalState?.selectedTeeTime?.facility?.logo_source && (
                  <img
                    className="tee-on-booking-portal-bc-facility-logo"
                    src={teeOnPortalState?.selectedTeeTime?.facility?.logo_source}
                    alt={t("Facility Logo")}
                  />
                )}
              </span>
              <div className="flex flex-col gap-1 tee-on-booking-portal-bc-text-align-left-responsive">
                <p className="tee-on-booking-portal-booking-timer-course-name">
                  {teeOnPortalState?.selectedTeeTime?.facility?.long_name}
                </p>
                <p className="tee-on-booking-portal-booking-timer-address">{`${teeOnPortalState?.selectedTeeTime?.facility?.address_line_1}, ${teeOnPortalState?.selectedTeeTime?.facility?.city}, ${teeOnPortalState?.selectedTeeTime?.facility?.province_name}, ${teeOnPortalState?.selectedTeeTime?.facility?.postal}`}</p>
              </div>
            </div>

            {favouriteLoading ? (
              <div style={{ height: "24px", width: "24px" }}>
                <Spin />
              </div>
            ) : (
              <OverlayTrigger
                placement="auto"
                overlay={
                  <Tooltip id="facilityFavouritesToolTip">
                    {teeOnPortalState?.selectedTeeTime?.facility?.favourite
                      ? t("Remove from favourites")
                      : t("Add to favourites")}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon
                  onClick={
                    teeOnPortalState?.selectedTeeTime?.facility?.favourite
                      ? () => handleRemoveFromFavourites()
                      : () => handleAddToFavourites()
                  }
                  size="1x"
                  fixedWidth
                  className={classNames("tee-on-booking-portal-bc-unfavourite", {
                    "tee-on-booking-portal-bc-favourite": teeOnPortalState?.selectedTeeTime?.facility?.favourite,
                  })}
                  icon={faStar}
                />
              </OverlayTrigger>
            )}
          </div>

          <div className="tee-on-booking-portal-bc-card-container">
            <Card>
              <Card.Section>
                <div className="tee-on-booking-portal-bc-booked-container tee-on-booking-portal-bc-flex-column">
                  <p className="tee-on-booking-portal-bc-booked-text tee-on-booking-portal-bc-booked-title-text-responsive">
                    {"Your tee time has been booked!"}
                  </p>
                  <p className="tee-on-booking-portal-bc-booked-text-subtext tee-on-booking-portal-bc-booked-sub-text-responsive">
                    {teeOnPortalState?.selectedTeeTime?.booking?.customer?.email &&
                    !teeOnPortalState?.selectedTeeTime?.booking?.customer?.email?.includes("guest.teeon.com") ? (
                      <>
                        {t('We&apos;ll send you a confirmation and receipt to')}
                        <span className="tee-on-booking-portal-bc-booked-text-confirmation-number tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {" "}
                          {teeOnPortalState?.selectedTeeTime?.booking?.customer?.email}{" "}
                        </span>
                        {t('shortly')}
                      </>
                    ) : (
                      "We'll send a confirmation and receipt to your email shortly"
                    )}
                  </p>
                  <div className="tee-on-booking-portal-bc-booked-confirmation-container">
                    <p className="tee-on-booking-portal-bc-booked-text-confirmation tee-on-booking-portal-bc-booked-sub-text-responsive">
                      {"Confirmation Number:"}
                    </p>
                    <p className="tee-on-booking-portal-bc-booked-text-confirmation-number tee-on-booking-portal-bc-booked-sub-text-responsive">
                      {teeOnPortalState?.selectedTeeTime?.booking?.confirmation_number}
                    </p>
                  </div>
                </div>
              </Card.Section>
            </Card>
          </div>

          <div className="tee-on-booking-portal-bc-card-container">
            <Card>
              <Card.Section>
                <p className="tee-on-booking-portal-bc-booking-details-card-title">{"Booking Details"}</p>
                <div className="tee-on-booking-portal-bc-booking-details-content">
                  <div className="tee-on-booking-portal-bc-booking-details-container">
                    <p className="tee-on-booking-portal-bc-booking-details-title tee-on-booking-portal-bc-booked-title-text-responsive">
                      {t("Booked By")}
                    </p>
                    <div className="tee-on-booking-portal-bc-booking-details-golfer-details tee-on-booking-portal-bc-flex-column tee-on-booking-portal-bc-flex-grow">
                      <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                        {teeOnPortalState?.selectedTeeTime?.booking?.customer?.full_name ??
                          `${teeOnPortalState?.selectedTeeTime?.booking?.first_name} ${teeOnPortalState?.selectedTeeTime?.booking?.last_name}`}
                      </p>
                      {!teeOnPortalState?.selectedTeeTime?.booking?.customer?.email?.includes("guest.teeon.com") && (
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {teeOnPortalState?.selectedTeeTime?.booking?.customer?.email}
                        </p>
                      )}
                      <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                        {teeOnPortalState?.selectedTeeTime?.booking?.customer?.phone}
                      </p>
                    </div>
                  </div>

                  <div className="tee-on-booking-portal-bc-booking-details-container">
                    <p className="tee-on-booking-portal-bc-booking-details-title tee-on-booking-portal-bc-booked-title-text-responsive">
                      {"Order Summary"}
                    </p>
                    <div className="tee-on-booking-portal-bc-booking-details-order-summary tee-on-booking-portal-bc-flex-column tee-on-booking-portal-bc-flex-grow">
                      <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold tee-on-booking-portal-bc-booked-sub-text-responsive">
                        {teeOnPortalState?.selectedTeeTime?.facility?.long_name}
                      </p>
                      <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                        {teeOnPortalState?.selectedTeeTime?.teeTime?.formattedDate} •{" "}
                        {teeOnPortalState?.selectedTeeTime?.teeTime?.formattedStartTime}
                      </p>
                      <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                        {teeOnPortalState?.selectedTeeTime?.booking?.holes} {"Holes •"}{" "}
                        {teeOnPortalState?.selectedTeeTime?.booking?.quantity}
                        {teeOnPortalState?.selectedTeeTime?.booking?.quantity > 1 ? " Golfers " : " Golfer "}•{" "}
                        {teeOnPortalState?.selectedTeeTime?.booking?.power_cart_quantity === 0
                          ? "Walking"
                          : teeOnPortalState?.selectedTeeTime?.booking?.power_cart_quantity > 0
                          ? teeOnPortalState?.selectedTeeTime?.booking?.power_cart_quantity === 1
                            ? `${teeOnPortalState?.selectedTeeTime?.booking?.power_cart_quantity} Power Cart `
                            : `${teeOnPortalState?.selectedTeeTime?.booking?.power_cart_quantity} Power Carts `
                          : null}
                      </p>
                      <p className="tee-on-booking-portal-bc-add-players-button" onClick={editBooking}>
                        <FontAwesomeIcon icon={["far", "plus"]} />{" "}
                        {teeOnPortalState?.selectedTeeTime?.booking?.tee_time?.quantity_remaining === 0
                          ? t("Edit Players")
                          : t("Add Players")}
                      </p>
                      <div>
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold tee-on-booking-portal-bc-booked-sub-text-responsive tee-on-booking-portal-bc-order-line-item">
                          {t("Items")}
                        </p>
                        {cartState?.line_items?.map((lineItem: Record<string, any>, index: number) => {
                          return (
                            <div key={index} className="tee-on-booking-portal-bc-order-line-item">
                              <OrderSummaryLineItem
                                lineItemQuantity={lineItem.quantity}
                                lineItem={lineItem.product_title}
                                lineItemVariant={lineItem.variant_title}
                                prepaid={lineItem.pre_paid_required}
                                itemValue={
                                  <LocaleCurrency currency={cartState?.currency} amount={lineItem.subtotal_price} />
                                }
                                className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="tee-on-booking-portal-bc-booking-details-container">
                    <p className="tee-on-booking-portal-bc-booking-details-title tee-on-booking-portal-bc-booked-title-text-responsive">
                      {"Order Total"}
                    </p>
                    <div className="tee-on-booking-portal-bc-flex-column w-60 tee-on-booking-portal-bc-width-auto tee-on-booking-portal-bc-flex-grow">
                      {(teeOnPortalState?.selectedTeeTime?.teeTime?.credit_card_required ||
                        teeOnPortalState?.selectedTeeTime?.teeTime?.pre_paid_required) &&
                      paymentMethod?.brand &&
                      paymentMethod?.last4 ? (
                        <p className="tee-on-booking-portal-bc-booking-payment-card tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {String(paymentMethod?.brand).charAt(0).toUpperCase() + String(paymentMethod?.brand).slice(1)}{" "}
                          {"ending in"} {paymentMethod?.last4}
                        </p>
                      ) : (
                        <p></p>
                      )}
                      <div className="flex flex-row justify-between">
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {t("Subtotal")}
                        </p>
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                          <LocaleCurrency currency={cartState?.currency} amount={cartState?.subtotal_price} />
                        </p>
                      </div>
                      {cartState?.tax_lines?.map((taxLine: ITaxLine, index: number) => {
                        return (
                          <div key={index} className="flex flex-row justify-between">
                            <span className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                              {taxLine?.title}{" "}
                              {taxLine?.rate && (
                                <span className="tee-on-booking-portal-bc-booked-subtitle">{`${
                                  taxLine?.rate * 100
                                }%`}</span>
                              )}
                            </span>
                            <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                              <LocaleCurrency currency={cartState?.currency} amount={taxLine?.price} />
                            </p>
                          </div>
                        );
                      })}
                      <div className="flex flex-row justify-between">
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {"Order Total"}
                        </p>
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booked-sub-text-responsive">
                          <LocaleCurrency currency={cartState?.currency} amount={cartState?.total_price} />
                        </p>
                      </div>
                      <hr />
                      <div className="flex flex-row justify-between">
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold-heavy tee-on-booking-portal-bc-booked-sub-text-responsive">
                          {"Total Due at Course"}
                        </p>
                        <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold-heavy tee-on-booking-portal-bc-booked-sub-text-responsive">
                          <LocaleCurrency
                            currency={cartState?.currency}
                            amount={
                              cartState?.total_price_due
                                ? cartState?.total_price - cartState?.total_price_due
                                : cartState?.total_price
                            }
                          />
                        </p>
                      </div>
                      {cartState?.total_price_due ? (
                        <div className="flex flex-row justify-between">
                          <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold-heavy tee-on-booking-portal-bc-booked-sub-text-responsive">
                            {"Total Paid"}
                          </p>
                          <p className="tee-on-booking-portal-bc-booking-details-text tee-on-booking-portal-bc-booking-details-text-bold-heavy tee-on-booking-portal-bc-booked-sub-text-responsive">
                            <LocaleCurrency currency={cartState?.currency} amount={cartState?.total_price_due} />
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="tee-on-booking-portal-bc-tee-times-button">
                  <Button type="primary" size="medium" onClick={backToTeeTimes}>
                    {"Back to Tee Times"}
                  </Button>
                  {editBookingState && (
                    <Button type="secondary" size="medium" onClick={editBooking}>
                      {"Edit Booking"}
                    </Button>
                  )}
                </div>
              </Card.Section>
            </Card>
          </div>
        </div>
      </>
    )
  );
}
